import {
    Box,
    Button,
    Card, Checkbox, ColorPaletteProp,
    Divider,
    Grid,
    Stack,
    Typography,
} from "@mui/joy";
import React, {useEffect, useMemo, useState} from "react";
import {ChevronLeftIcon, ChevronRightIcon} from "lucide-react";
import {useTestRoom} from "../../../../../hooks/useTestRoom";
import {getAlphabeticalLetterOptions} from "../../../../../utils/getAlphabeticalLetterOptions";
import {QuestionGroup, RawQuestion} from "../../../../../models/question";
import {SectionName} from "../../../../../models/section";

type DraggableComponentProps = {
    isDragging: boolean,
    onDragStart: () => void,
    onDragEnd: () => void,
    value: string
}

const IeltsMatchingQuestionComponent = (props: {
    question: RawQuestion,
    questionGroupIndex: number,
    questionId: string,
    subSectionNumber: number,
    testId: string,
    sectionName: SectionName,
}) => {
    const testRoom = useTestRoom()
    const [draggingId, setDraggingId] = useState<number>()
    const [overId, setOverId] = useState<number>()
    const [over, setOver] = useState(false)
    const [optionValues, setOptionValues] = useState<string[]>(props.question?.metadata['optionValues'] || [])
    const [options] = useState<string[]>(props.question?.metadata['options'] || [])
    const [matchedValues, setMatchedValues] = useState<{
        [key: number]: string
    }>(testRoom.questionAttempts?.find(value => {
        if (!!props.questionGroupIndex)
            return value.questionId === props.questionId && value.questionGroupIndex === props.questionGroupIndex
        else
            return value.questionId === props.questionId
    })?.userAnswer || {})
    const previousQuestionsCount = testRoom.questions
        .filter(q => q.subSectionNumber < testRoom.currentSubsectionNumber)
        .reduce((sum, q) => sum + (q as QuestionGroup).questions.length, 0)
    const questionNumber = previousQuestionsCount + props.questionGroupIndex + 1

    const handleAnswerQuestion = (matchedValues: Record<number, string>) => {
        testRoom.answerQuestion(matchedValues, props.question, {
            sectionName: props.sectionName,
            testId: props.testId,
            questionGroupIndex: props.questionGroupIndex,
            questionId: props.questionId,
            isFromGroup: !!props.questionGroupIndex,
            contentType: "text"
        })
    }

    return <Stack paddingY={2} gap={3} maxWidth={{md: "100%"}}>
        <Stack>
            <Typography level={"title-lg"}>
                Question {questionNumber}
            </Typography>
            <Typography>
                {props.question.content}
            </Typography>
        </Stack>

        <Grid spacing={{md: 4, xs: 2}} container>
            <Grid xs={8}>
                <Stack gap={2}>
                    {options.map((value, index) => {
                        return <Stack justifyContent={{md: "space-between"}} direction={{md: "row"}} gap={2}
                                      alignItems={"center"}>
                            <Typography level={"body-sm"}>
                                {value}
                            </Typography>

                            <Card
                                color={(over && overId === index) || !!matchedValues[index] ? "primary" : "neutral"}
                                variant={(!matchedValues[index] && !(over && overId === index)) ? "outlined" : "soft"}
                                onDragOver={(e) => {
                                    e.preventDefault()
                                    setOver(true)
                                    setOverId(index)
                                }}
                                onDragLeave={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                        setOver(false)
                                        setOverId(null)
                                    }
                                }}
                                onDrop={() => {
                                    setMatchedValues(prevState => {
                                        if (prevState[index])
                                            setOptionValues(ps => {
                                                const ovSlice = ps.slice()
                                                ovSlice.push(prevState[index])
                                                delete prevState[index]
                                                return ovSlice
                                            })
                                        handleAnswerQuestion({...prevState, [index]: optionValues[draggingId]})
                                        return ({...prevState, [index]: optionValues[draggingId]})
                                    })

                                    setOver(false)
                                    setOverId(null)
                                }}
                                sx={{
                                    height: "1.5rem",
                                    padding: 0.5,
                                    width: {xs: "100%", md: "50%"},
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}>
                                <Typography textAlign={"center"} level={"body-xs"} textColor={"text.primary"}>
                                    {(over && overId === index) ? "" : matchedValues[index]}
                                </Typography>
                            </Card>

                        </Stack>

                    })}
                </Stack>
            </Grid>

            <Grid xs={4}>
                <Stack gap={2}>
                    {optionValues.map((value, index) =>
                        !Object.values(matchedValues).includes(value) &&
                        <DraggableComponent isDragging={index === draggingId}
                                            onDragStart={() => setDraggingId(index)}
                                            onDragEnd={() => setDraggingId(null)}
                                            value={value}/>)}
                </Stack>
            </Grid>

        </Grid>
    </Stack>
}

const DraggableComponent = (props: DraggableComponentProps) => {
    return <Card draggable
                 onDragStart={props.onDragStart}
                 onDragEnd={props.onDragEnd}
                 sx={{
                     boxShadow: "lg",
                     padding: 0.5,
                     height: "1.5rem",
                     justifyContent: "center",
                     alignItems: "center",
                     cursor: props.isDragging ? "grabbing" : "grab",
                     userSelect: "none",
                     opacity: props.isDragging ? 0.4 : 1,
                     transform: props.isDragging ? "scale(0.97)" : "scale(1)",
                     transition: "opacity 0.12s, transform 0.12s",
                 }} size={"sm"}>
        <Typography textAlign={"center"} level={"body-xs"} textColor={"text.primary"}>
            {props.value}
        </Typography>
    </Card>
}


export default IeltsMatchingQuestionComponent
